import { useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { currentUserId } from './auth.js';

/**
 * UAT-01 — the "Recent Projects" recency signal. A per-user (and inherently
 * per-device) localStorage log of trial interactions: visiting any
 * trial-scoped route counts as an interaction, and the Dashboard's Recent tab
 * shows the last week's worth, most-recent first. Purely presentational
 * convenience — never sent to the server, and recording failures are silent
 * (recency must never break navigation).
 */
export const RECENT_PROJECTS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const storageKey = (userId: string): string => `suvoda-uat.recent-projects.${userId}`;

/** trialId → last interaction (epoch ms). */
type InteractionLog = Readonly<Record<string, number>>;

const readLog = (userId: string): InteractionLog => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' && Number.isFinite(entry[1]),
      ),
    );
  } catch {
    return {};
  }
};

const withinWindow = (lastMs: number, nowMs: number): boolean =>
  nowMs - lastMs < RECENT_PROJECTS_WINDOW_MS;

/** Record "the viewer touched this trial just now". */
export const recordProjectInteraction = (
  userId: string | undefined,
  trialId: string,
  nowMs: number,
): void => {
  if (userId === undefined) return;
  try {
    const live = Object.fromEntries(
      Object.entries(readLog(userId)).filter(([, lastMs]) => withinWindow(lastMs, nowMs)),
    );
    localStorage.setItem(storageKey(userId), JSON.stringify({ ...live, [trialId]: nowMs }));
  } catch {
    // Quota/privacy-mode failures lose recency, nothing else.
  }
};

/** Trial ids the user interacted with in the last week, most-recent first. */
export const recentProjectIds = (userId: string | undefined, nowMs: number): readonly string[] => {
  if (userId === undefined) return [];
  return Object.entries(readLog(userId))
    .filter(([, lastMs]) => withinWindow(lastMs, nowMs))
    .sort(([, a], [, b]) => b - a)
    .map(([trialId]) => trialId);
};

/**
 * Mounted once in the root route — invisible, renders nothing. Any route
 * carrying a `trialId` search param (upload, verification, assembly, cases)
 * marks that trial as interacted-with.
 */
export const RecentProjectsRecorder = (): null => {
  const location = useRouterState({ select: (state) => state.location });
  useEffect(() => {
    const search = location.search as { trialId?: string };
    if (typeof search.trialId === 'string' && search.trialId.length > 0) {
      recordProjectInteraction(currentUserId(), search.trialId, Date.now());
    }
  }, [location.pathname, location.searchStr]);
  return null;
};
